<script setup>
import { useUserStore, useListStore } from "@/stores";
import useVuelidate from "@vuelidate/core";
import { email, helpers, required } from "@vuelidate/validators";
import { computed, onMounted, ref } from "vue";

const userStore = useUserStore();
const listStore = useListStore();

const form = ref({});

const loading = ref(false);

const leftLists = computed(() => ({
  countries: listStore.countries,
  organizations: listStore.organizations,
  referSources: listStore.refer,
  branches: listStore.branches,
  services: listStore.services,
  educationSections: listStore.educationSections,
  professionalFields: listStore.professionalFields,
  businessSectors: listStore.businessSectors,
}));

const rightLists = computed(() => ({
  computerSkills: listStore.computerSkills,
  sectors: listStore.sectors,
  softSkillTrainings: listStore.softSkillTrainings,
  thematicAreas: listStore.thematicAreas,
  coursesSections: listStore.courseSections,
  courseTypes: listStore.courseTypes,
  emplServices: listStore.emplServices,
  professions: listStore.professions,
}));

function createdItem(i, type) {
  console.log("createdItem");
  console.log(`createdItem(${type}): ${JSON.stringify(i)}`);
  switch (type) {
    case "organizations":
      listStore.createOrganization(i);
      break;
    case "countries":
      listStore.createCountry(i);
      break;
    case "referSources":
      listStore.createReferSource(i);
      break;
    case "branches":
      listStore.createBranch(i);
      break;
    case "services":
      listStore.createService(i);
      break;
    case "educationSections":
      listStore.createEducationSection(i);
      break;
    case "professionalFields":
      listStore.createProfessionalField(i);
      break;
    case "businessSectors":
      listStore.createBusinessSector(i);
      break;
    case "computerSkills":
      listStore.createComputerSkill(i);
      break;
    case "sectors":
      listStore.createSector(i);
      break;
    case "softSkillTrainings":
      listStore.createSoftSkillTraining(i);
      break;
    case "thematicAreas":
      listStore.createThematicArea(i);
      break;
    case "coursesSections":
      listStore.createCourseSection(i);
      break;
    case "courseTypes":
      listStore.createCourseType(i);
      break;
    case "emplServices":
      listStore.createEmplService(i);
      break;
    case "professions":
      console.log("createdItem | case professions");
      listStore.createProfession(i);
      break;
    default:
      console.warn(`Unhandled create type: ${type}`);
  }
}

function updatedItem(i, type) {
  console.log(`updatedItem(${type}): ${JSON.stringify(i)}`);
  switch (type) {
    case "organizations":
      listStore.updateOrganization(i);
      break;
    case "countries":
      listStore.updateCountry(i);
      break;
    case "referSources":
      listStore.updateReferSource(i);
      break;
    case "branches":
      listStore.updateBranch(i);
      break;
    case "services":
      listStore.updateService(i);
      break;
    case "educationSections":
      listStore.updateEducationSection(i);
      break;
    case "professionalFields":
      listStore.updateProfessionalField(i);
      break;
    case "businessSectors":
      listStore.updateBusinessSector(i);
      break;
    case "computerSkills":
      listStore.updateComputerSkill(i);
      break;
    case "sectors":
      listStore.updateSector(i);
      break;
    case "softSkillTrainings":
      listStore.updateSoftSkillTraining(i);
      break;
    case "thematicAreas":
      listStore.updateThematicArea(i);
      break;
    case "coursesSections":
      listStore.updateCourseSection(i);
      break;
    case "courseTypes":
      listStore.updateCourseType(i);
      break;
    case "emplServices":
      listStore.updateEmplService(i);
      break;
    case "professions":
      listStore.updateProfession(i);
      break;
    default:
      console.warn(`Unhandled update type: ${type}`);
  }
}

function deletedItem(i, type) {
  console.log(`deletedItem(${type}): ${JSON.stringify(i)}`);
  switch (type) {
    case "organizations":
      listStore.deleteOrganization(i);
      break;
    case "countries":
      listStore.deleteCountry(i);
      break;
    case "referSources":
      listStore.deleteReferSource(i);
      break;
    case "branches":
      listStore.deleteBranch(i);
      break;
    case "services":
      listStore.deleteService(i);
      break;
    case "educationSections":
      listStore.deleteEducationSection(i);
      break;
    case "professionalFields":
      listStore.deleteProfessionalField(i);
      break;
    case "businessSectors":
      listStore.deleteBusinessSector(i);
      break;
    case "computerSkills":
      listStore.deleteComputerSkill(i);
      break;
    case "sectors":
      listStore.deleteSector(i);
      break;
    case "softSkillTrainings":
      listStore.deleteSoftSkillTraining(i);
      break;
    case "thematicAreas":
      listStore.deleteThematicArea(i);
      break;
    case "coursesSections":
      listStore.deleteCourseSection(i);
      break;
    case "courseTypes":
      listStore.deleteCourseType(i);
      break;
    case "emplServices":
      listStore.deleteEmplService(i);
      break;
    case "professions":
      listStore.deleteProfession(i);
      break;
    default:
      console.warn(`Unhandled delete type: ${type}`);
  }
}

function formatLabel(str) {
  return str
    .replace(/([A-Z])/g, " $1") // Insert space before capital letters
    .replace(/^./, (s) => s.toUpperCase()) // Capitalize first letter
    .trim(); // Remove leading/trailing spaces
}

onMounted(async () => {
  await listStore.fetchListHashes();

  await listStore.fetchCountries();
  await listStore.fetchOrganizations();
  await listStore.fetchRefer();
  await listStore.fetchBranches();
  await listStore.fetchServices();
  await listStore.fetchEducationSections();
  await listStore.fetchProfessionalFields();
  await listStore.fetchBusinessSectors();

  await listStore.fetchComputerSkills();
  await listStore.fetchSectors();
  await listStore.fetchSoftSkillTrainings();
  await listStore.fetchThematicAreas();
  await listStore.fetchCourseSections();
  await listStore.fetchCourseTypes();
  await listStore.fetchEmplServices();
  await listStore.fetchProfessions();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12" sm="5">
        <v-expansion-panels multiple>
          <v-expansion-panel
            v-for="(list, listName) in leftLists"
            :key="listName"
            :value="listName"
          >
            <v-expansion-panel-title
              collapse-icon="mdi-minus"
              expand-icon="mdi-plus"
            >
              <h3>{{ formatLabel(listName) }}</h3>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <EditableList
                :items="list"
                :deletable="listName != 'branches'"
                @updated="(item) => updatedItem(item, listName)"
                @created="(item) => createdItem(item, listName)"
                @deleted="(item) => deletedItem(item, listName)"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>

      <v-col cols="12" sm="5" offset="0" offset-sm="1">
        <v-expansion-panels multiple>
          <v-expansion-panel
            v-for="(list, listName) in rightLists"
            :key="listName"
            :value="listName"
          >
            <v-expansion-panel-title
              collapse-icon="mdi-minus"
              expand-icon="mdi-plus"
            >
              <h3>{{ formatLabel(listName) }}</h3>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <EditableList
                :items="list"
                @updated="(item) => updatedItem(item, listName)"
                @created="(item) => createdItem(item, listName)"
                @deleted="(item) => deletedItem(item, listName)"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
.image-preview {
  img {
    max-width: 150px;
    border-radius: 8px;
  }
}
</style>
